import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { logConsentToDb } from "@/lib/consentLog";
import { Separator } from "@/components/ui/separator";

interface CookiePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CookiePreferencesModal({ open, onOpenChange }: CookiePreferencesModalProps) {
  const {
    consent,
    setConsent,
    acceptAll,
    rejectAll,
    setBannerDismissed,
    setShowBanner,
  } = useCookieConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);
  const prevOpen = useRef(false);

  // Sync local state when modal opens or consent changes
  useEffect(() => {
    if (open) {
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
    }
  }, [open, consent?.analytics, consent?.marketing]);

  // Focus trap: when opening, focus first focusable element (Radix Dialog traps focus; ESC closes)
  useEffect(() => {
    if (open && !prevOpen.current) {
      prevOpen.current = true;
      const t = setTimeout(() => {
        const portal = document.querySelector('[data-radix-dialog-portal]');
        const first = portal?.querySelector<HTMLElement>(
          'button:not([aria-hidden]), [href], input:not([type="hidden"]), [tabindex="0"]'
        );
        (first as HTMLElement | null)?.focus();
      }, 80);
      return () => clearTimeout(t);
    }
    if (!open) prevOpen.current = false;
  }, [open]);

  const handleSave = () => {
    const state = {
      essential: true,
      analytics,
      marketing,
      version: consent?.version ?? "1",
      updatedAt: new Date().toISOString(),
    };
    setConsent({ analytics, marketing });
    setBannerDismissed(true);
    setShowBanner(false);
    logConsentToDb(state);
    onOpenChange(false);
  };

  const handleAcceptAll = () => {
    acceptAll();
    onOpenChange(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        aria-describedby="cookie-modal-desc"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle id="cookie-modal-title">Cookie preferences</DialogTitle>
          <DialogDescription id="cookie-modal-desc">
            Choose which categories of cookies you allow. Essential cookies are
            required for the site to work and cannot be disabled.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/40 bg-muted/30 px-4 py-3">
            <div>
              <Label htmlFor="essential" className="text-sm font-medium">
                Essential
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Required for login, security, and core features.
              </p>
            </div>
            <Switch id="essential" checked disabled aria-label="Essential cookies (always on)" />
          </div>
          <Separator />
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/40 px-4 py-3">
            <div>
              <Label htmlFor="analytics" className="text-sm font-medium">
                Analytics
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Help us understand how you use the product.
              </p>
            </div>
            <Switch
              id="analytics"
              checked={analytics}
              onCheckedChange={(v) => setAnalytics(v)}
              aria-label="Enable analytics cookies"
            />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/40 px-4 py-3">
            <div>
              <Label htmlFor="marketing" className="text-sm font-medium">
                Marketing
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Used for ads and campaign measurement (if applicable).
              </p>
            </div>
            <Switch
              id="marketing"
              checked={marketing}
              onCheckedChange={(v) => setMarketing(v)}
              aria-label="Enable marketing cookies"
            />
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={handleRejectAll}>
            Rifiuta tutti
          </Button>
          <Button variant="outline" size="sm" onClick={handleAcceptAll}>
            Accetta tutti
          </Button>
          <Button size="sm" onClick={handleSave}>
            Salva preferenze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
